import { useState } from "react";
import { Button, Card, Pill } from "./ui";
import { Ic } from "./icons";
import { useNav } from "../context/NavContext";
import {
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
} from "../lib/auth";

export const SignInDialog = ({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { go } = useNav();

  function openLegal(hash: string) {
    window.location.hash = `#${hash}`;
    go("legal");
    onClose();
  }

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signin") {
        await signInWithEmail(email, password);
      } else {
        await signUpWithEmail(email, password);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function google() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-[440px] p-7 shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <Pill tone="lime" icon={<Ic.Bolt size={12} />}>
            {mode === "signin" ? "Se connecter" : "Créer un compte"}
          </Pill>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500"
          >
            <Ic.Cross size={14} />
          </button>
        </div>
        <h2 className="mt-3 text-[22px] font-semibold tracking-tight">
          {mode === "signin"
            ? "Ravi de vous revoir."
            : "Bienvenue sur Arpentia."}
        </h2>
        <p className="mt-1 text-[13px] text-ink-600">
          {mode === "signin"
            ? "Accédez à votre dashboard et vos fiches."
            : "Créez un compte en 10 secondes — 1 fiche offerte, 7 j."}
        </p>

        <Button
          onClick={google}
          variant="soft"
          className="mt-5 w-full"
          disabled={busy}
          icon={
            <svg width="16" height="16" viewBox="0 0 18 18">
              <path
                d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92a8.78 8.78 0 0 0 2.68-6.61Z"
                fill="#4285F4"
              />
              <path
                d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.87-3.04.87-2.34 0-4.32-1.58-5.03-3.7H.92v2.33A9 9 0 0 0 9 18Z"
                fill="#34A853"
              />
              <path
                d="M3.97 10.73A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.19.29-1.73V4.94H.92A9 9 0 0 0 0 9c0 1.45.35 2.82.92 4.06l3.05-2.33Z"
                fill="#FBBC05"
              />
              <path
                d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .92 4.94l3.05 2.33C4.68 5.15 6.66 3.58 9 3.58Z"
                fill="#EA4335"
              />
            </svg>
          }
        >
          Continuer avec Google
        </Button>

        <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-widest text-ink-400">
          <div className="flex-1 h-px bg-ink-200" />
          ou
          <div className="flex-1 h-px bg-ink-200" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-[11.5px] text-ink-500">Email</span>
            <input
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="camille@hexagone.fr"
              className="mt-1 w-full bg-paper hairline rounded-xl2 h-11 px-3 text-[13.5px] outline-none focus:ring-2 focus:ring-lime-300"
            />
          </label>
          <label className="block">
            <span className="text-[11.5px] text-ink-500">Mot de passe</span>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1 w-full bg-paper hairline rounded-xl2 h-11 px-3 text-[13.5px] outline-none focus:ring-2 focus:ring-lime-300"
            />
          </label>

          {error && (
            <div className="text-[12px] text-rose-700 bg-rose-50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="dark"
            className="w-full"
            disabled={busy}
            iconRight={<Ic.Arrow size={14} />}
          >
            {busy
              ? "…"
              : mode === "signin"
                ? "Se connecter"
                : "Créer mon compte"}
          </Button>
        </form>

        <div className="mt-4 text-center text-[12.5px] text-ink-600">
          {mode === "signin" ? (
            <>
              Pas de compte ?{" "}
              <button
                onClick={() => setMode("signup")}
                className="text-ink-900 font-medium hover:underline"
              >
                Créer un compte
              </button>
            </>
          ) : (
            <>
              Déjà client ?{" "}
              <button
                onClick={() => setMode("signin")}
                className="text-ink-900 font-medium hover:underline"
              >
                Se connecter
              </button>
            </>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-ink-200/70 text-center text-[11.5px] text-ink-500 leading-relaxed">
          En {mode === "signin" ? "vous connectant" : "créant un compte"}, vous
          acceptez nos{" "}
          <button
            type="button"
            onClick={() => openLegal("cgu")}
            className="underline decoration-ink-300 underline-offset-2 hover:text-ink-900"
          >
            CGU
          </button>
          {" "}et notre{" "}
          <button
            type="button"
            onClick={() => openLegal("confidentialite")}
            className="underline decoration-ink-300 underline-offset-2 hover:text-ink-900"
          >
            politique de confidentialité
          </button>
          .
        </div>
      </Card>
    </div>
  );
};
